import { usePortalAccess } from '../../components/PortalAccess';
import React, { useEffect, useRef, useState } from 'react'
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
  PieChart, Pie,
} from 'recharts'
import {
  getTGKpi, getTGChartData, getTGMoM, getTGPosts, syncTG,
  getCategories, updatePost,
} from '../../utils/smmApi'
import { KPICard } from '../../components/smm/KPICard'
import { PeriodSelector } from '../../components/smm/PeriodSelector'
import { MetricTooltip } from '../../components/smm/MetricTooltip'
import { formatNumber, formatFull, formatDate } from '../../utils/smmFormat'
import styles from './SmmVK.module.css'

const PLATFORM_COLOR = '#2AABEE'
const PIE_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#a855f7', '#84cc16']

const PERIOD_OPTIONS = [
  { label: '7д', value: 7 },
  { label: '30д', value: 30 },
  { label: 'Квартал', value: 90 },
  { label: 'Год', value: 365 },
  { label: 'Все', value: 1825 },
]

const MOM_OPTIONS = [
  { label: '3 мес.', value: 3 },
  { label: '6 мес.', value: 6 },
  { label: 'Год', value: 12 },
]

// Tooltip metadata for all metrics
const METRIC_META = {
  'Подписчики':           { desc: 'Количество подписчиков канала на конец периода', src: 'API' },
  'ER':                   { desc: '(реакции + комм + репосты) / подписчики × 100 — средний по постам', src: 'Расчёт' },
  'ERViews':              { desc: '(реакции + комм + репосты) / просмотры × 100 — средний по постам', src: 'Расчёт' },
  'ViewsRate':            { desc: 'Средние просмотры поста / подписчики × 100', src: 'Расчёт' },
  'Вкл. уведомления':     { desc: '% подписчиков, не отключивших уведомления (ручной ввод)', src: 'Вручную' },
  'Реакции':              { desc: 'Суммарные реакции на посты за период', src: 'API' },
  'Комментарии':          { desc: 'Суммарные комментарии на посты за период', src: 'API' },
  'Репосты':              { desc: 'Суммарные пересылки (forwards) постов за период', src: 'API' },
  'Всего просмотров':       { desc: 'Суммарные просмотры всех постов за период / за месяц', src: 'API' },
  'Ср. просмотров':         { desc: 'Суммарные просмотры / количество постов', src: 'Расчёт' },
  'Кол-во постов':          { desc: 'Количество опубликованных постов за период', src: 'API' },
  'Охват за 24ч':           { desc: 'Среднее кол-во просмотров поста за первые 24 часа после публикации', src: 'API' },
  'Просмотры':              { desc: 'Текущие просмотры поста', src: 'API' },
  'Просмотры за 24ч':       { desc: 'Просмотры поста через ~24ч после публикации (снапшот)', src: 'API' },
  'Изменение подп.':        { desc: 'Подписчики конец месяца − предыдущий месяц', src: 'Расчёт' },
  'Ср. просмотров на пост': { desc: 'Всего просмотров / кол-во постов', src: 'Расчёт' },
}

function TT({ k }) {
  const m = METRIC_META[k]
  if (!m) return null
  return <MetricTooltip description={m.desc} source={m.src} />
}

export default function SmmTelegram() {
  const { permissions } = usePortalAccess();
  const canManage = permissions['smm.manage'] === true;
  const [kpi, setKpi] = useState(null)
  const [chartData, setChartData] = useState([])

  const [momData, setMomData] = useState(null)
  const momTableRef = useRef(null)
  const [posts, setPosts] = useState([])
  const [postsTotal, setPostsTotal] = useState(0)
  const [postsPage, setPostsPage] = useState(1)
  const [loadingMore, setLoadingMore] = useState(false)
  const [period, setPeriod] = useState(30)
  const [momMonths, setMomMonths] = useState(6)
  const [syncing, setSyncing] = useState(false)
  const [syncMsg, setSyncMsg] = useState('')
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState([])
  const [postsCategoryId, setPostsCategoryId] = useState(null)
  const [postsSortBy, setPostsSortBy] = useState('published_at')
  const [postsSortDir, setPostsSortDir] = useState('desc')
  const [catSaveMsg, setCatSaveMsg] = useState(null)
  const momFirstRun = useRef(true)

  useEffect(() => {
    if (!momData?.months?.length) return undefined
    const frame = requestAnimationFrame(() => {
      if (momTableRef.current) momTableRef.current.scrollLeft = momTableRef.current.scrollWidth
    })
    return () => cancelAnimationFrame(frame)
  }, [momData])

  const periodDays = typeof period === 'number' ? period : 30
  const chartSeriesData = chartData.map((row) => ({
    ...row,
    subscribed: row.subscribed ?? 0,
    unsubscribed: row.unsubscribed ?? 0,
    likes: row.likes ?? 0,
    comments: row.comments ?? 0,
    reposts: row.reposts ?? 0,
    views: row.views ?? 0,
  }))

  useEffect(() => {
    Promise.allSettled([
      getTGKpi({ days: periodDays }),
      getTGChartData(periodDays),
      getTGMoM(momMonths),
      getCategories(),
    ])
      .then(([kRes, cRes, mRes, catsRes]) => {
        if (kRes.status === 'fulfilled') setKpi(kRes.value)
        else console.error(kRes.reason)
        if (cRes.status === 'fulfilled') setChartData(cRes.value)
        else console.error(cRes.reason)
        if (mRes.status === 'fulfilled') setMomData(mRes.value)
        else console.error(mRes.reason)
        if (catsRes.status === 'fulfilled') setCategories(catsRes.value)
        else console.error(catsRes.reason)
      })
      .finally(() => setLoading(false))
  }, [periodDays])

  // Load posts page 1 when filters change (replaces list)
  useEffect(() => {
    setPostsPage(1)  // reset page counter for future "load more"
    getTGPosts({
      days: periodDays,
      page: 1,
      per_page: 10,
      ...(postsCategoryId ? { category_id: postsCategoryId } : {}),
      sort_by: postsSortBy,
      sort_dir: postsSortDir,
    })
      .then(d => { setPosts(d.items); setPostsTotal(d.total) })
      .catch(console.error)
  }, [periodDays, postsCategoryId, postsSortBy, postsSortDir])

  const handleLoadMorePosts = async () => {
    const nextPage = postsPage + 1
    setPostsPage(nextPage)
    setLoadingMore(true)
    try {
      const d = await getTGPosts({
        days: periodDays,
        page: nextPage,
        per_page: 10,
        ...(postsCategoryId ? { category_id: postsCategoryId } : {}),
        sort_by: postsSortBy,
        sort_dir: postsSortDir,
      })
      setPosts(prev => [...prev, ...d.items])
      setPostsTotal(d.total)
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    if (momFirstRun.current) { momFirstRun.current = false; return }
    getTGMoM(momMonths).then(setMomData).catch(console.error)
  }, [momMonths])

  const handleSync = async () => {
    setSyncing(true)
    setSyncMsg('')
    try {
      const r = await syncTG()
      setSyncMsg(r.ok ? `Синхронизировано: ${r.synced_posts} постов` : `Ошибка: ${r.error}`)
      // Reload data
      const [k, c] = await Promise.all([
        getTGKpi({ days: periodDays }),
        getTGChartData(periodDays),
      ])
      setKpi(k); setChartData(c)
    } catch (e) {
      setSyncMsg('Ошибка: ' + (e?.response?.data?.detail || e?.message))
    } finally {
      setSyncing(false)
    }
  }

  const handleSort = (col) => {
    if (postsSortBy === col) {
      setPostsSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setPostsSortBy(col)
      setPostsSortDir('desc')
    }
  }

  const handleCategoryChange = async (postId, categoryId) => {
    setCatSaveMsg(null)
    try {
      await updatePost(postId, { category_id: categoryId })
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, category_id: categoryId } : p))
      setCatSaveMsg('ok')
      setTimeout(() => setCatSaveMsg(null), 1500)
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Ошибка'
      setCatSaveMsg('err:' + detail)
    }
  }

  // Pie chart data computed from current page posts (same as VK)
  const catCounts = categories.reduce((acc, cat) => {
    const count = posts.filter((p) => p.category_id === cat.id).length
    if (count > 0) acc.push({ name: cat.name, value: count, color: cat.color })
    return acc
  }, [])
  const uncategorized = posts.filter((p) => !p.category_id).length
  if (uncategorized > 0) catCounts.push({ name: 'Без рубрики', value: uncategorized, color: '#475569' })

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <span className="spinner" />
      </div>
    )
  }

  const noData = !kpi || Object.keys(kpi).length === 0 || kpi.posts_count === 0
  const d = kpi?.delta ?? {}

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: PLATFORM_COLOR, flexShrink: 0 }} />
          <div>
            <h1 className={styles.title}>Telegram</h1>
            <p className={styles.subtitle}>TGStat · данные за последние {periodDays} дней</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <PeriodSelector options={PERIOD_OPTIONS} value={period} onChange={setPeriod} />
          {canManage && (
            <button
              onClick={handleSync}
              disabled={syncing}
              style={{
                padding: '6px 14px', fontSize: 12, borderRadius: 6,
                background: PLATFORM_COLOR, color: '#fff', border: 'none',
                cursor: syncing ? 'not-allowed' : 'pointer', opacity: syncing ? 0.7 : 1,
              }}
            >
              {syncing ? 'Синхронизация…' : 'Синхронизировать'}
            </button>
          )}
        </div>
      </div>
      {syncMsg && (
        <div style={{ fontSize: 12, color: syncMsg.startsWith('Ошибка') ? 'var(--danger)' : 'var(--success)', marginTop: -12 }}>
          {syncMsg}
        </div>
      )}

      {noData ? (
        <div style={{ padding: 40, maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✈️</div>
          <h2 style={{ marginBottom: 8 }}>Нет данных Telegram</h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Укажите <strong>TGSTAT_CHANNEL_ID</strong> в настройках и нажмите «Синхронизировать».
          </p>
        </div>
      ) : (
        <>
          {/* ── KPI Cards 3×4 ─────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            {/* Row 1 */}
            <KPICard
              label="Подписчики"
              value={formatFull(kpi.subscribers)}
              subvalue={kpi.subscriber_change !== null && kpi.subscriber_change !== undefined
                ? `${kpi.subscriber_change >= 0 ? '+' : ''}${formatNumber(kpi.subscriber_change)}`
                : undefined}
              subvalueColor={kpi.subscriber_change > 0 ? 'positive' : kpi.subscriber_change < 0 ? 'negative' : 'neutral'}
              tooltip="Количество подписчиков канала на конец периода"
              source="API"
              delta={d.subscribers}
            />
            <KPICard label="ER" value={kpi.er ? `${kpi.er}%` : '—'}
              tooltip="(реакции + комм + репосты) / подписчики × 100, средний по постам"
              source="Расчёт" delta={d.er} />
            <KPICard label="ERViews" value={kpi.erviews ? `${kpi.erviews}%` : '—'}
              tooltip="(реакции + комм + репосты) / просмотры × 100, средний по постам"
              source="Расчёт" delta={d.erviews} />
            <KPICard label="ViewsRate" value={kpi.views_rate ? `${kpi.views_rate}%` : '—'}
              tooltip="Средние просмотры поста / подписчики × 100"
              source="Расчёт" delta={d.views_rate} />

            {/* Row 2 */}
            <KPICard label="Вкл. уведомления"
              value={kpi.notifications_on_pct !== null && kpi.notifications_on_pct !== undefined
                ? `${kpi.notifications_on_pct}%` : '—'}
              tooltip="% подписчиков, не отключивших уведомления (ручной ввод в Настройках)"
              source="Вручную" />
            <KPICard label="Реакции" value={formatNumber(kpi.total_likes)}
              tooltip="Суммарные реакции на посты за период" source="API" delta={d.total_likes} />
            <KPICard label="Комментарии" value={formatNumber(kpi.total_comments)}
              tooltip="Суммарные комментарии за период" source="API" delta={d.total_comments} />
            <KPICard label="Репосты" value={formatNumber(kpi.total_reposts)}
              tooltip="Суммарные пересылки (forwards) за период" source="API" delta={d.total_reposts} />

            {/* Row 3 */}
            <KPICard label="Всего просмотров" value={formatNumber(kpi.total_views)}
              tooltip="Суммарные просмотры всех постов за период" source="API" delta={d.total_views} />
            <KPICard label="Ср. просмотров" value={formatNumber(kpi.avg_views)}
              tooltip="Всего просмотров / количество постов" source="Расчёт" delta={d.avg_views} />
            <KPICard label="Кол-во постов" value={kpi.posts_count}
              tooltip="Количество опубликованных постов за период" source="API" delta={d.posts_count} />
            <KPICard label="Охват за 24ч"
              value={kpi.adv_post_reach_24h ? formatNumber(kpi.adv_post_reach_24h) : '—'}
              tooltip="Среднее количество просмотров поста за первые 24 часа после публикации"
              source="API" />
          </div>

          {/* ── Charts row 1: Subscribers (wide) + Views by day (narrow) ── */}
          {chartData.length > 0 && (
            <div className={styles.chartsRow}>
              <div className={styles.chartCardWide}>
                <div className={styles.chartHeader}>
                  <span className={styles.chartTitle}>Динамика подписчиков</span>
                  <span className={styles.chartHint}>подписки / отписки / итого</span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartSeriesData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
                    <YAxis yAxisId="left" width={42} tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                      domain={['auto', 'auto']}
                      tickFormatter={(v) => v.toLocaleString('ru-RU')} />
                    <YAxis yAxisId="right" width={24} orientation="right" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                    <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }}
                      formatter={(v, name) => [
                        typeof v === 'number' ? v.toLocaleString('ru-RU') : v, name,
                      ]} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="left" type="monotone" dataKey="subscribers" stroke={PLATFORM_COLOR}
                      dot={false} strokeWidth={2} name="Подписчики" connectNulls />
                    <Line yAxisId="right" type="monotone" dataKey="subscribed" stroke="#22c55e"
                      dot={{ r: 2 }} strokeWidth={1.5} name="+Подписки" connectNulls />
                    <Line yAxisId="right" type="monotone" dataKey="unsubscribed" stroke="#ef4444"
                      dot={{ r: 2 }} strokeWidth={1.5} name="−Отписки" connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                  <span className={styles.chartTitle}>Просмотры по дням</span>
                  <span className={styles.chartHint}>сумма просмотров постов</span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartSeriesData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
                    <YAxis width={36} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                    <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }}
                      formatter={(v) => [typeof v === 'number' ? v.toLocaleString('ru-RU') : v, 'Просмотры']} />
                    <Line type="monotone" dataKey="views" stroke={PLATFORM_COLOR} dot={false} strokeWidth={2} name="Просмотры" connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* ── Charts row 2: Reactions (wide) + Posts by category (narrow) ─ */}
          {chartData.length > 0 && (
            <div className={styles.chartsRow2}>
              <div className={styles.chartCardWide}>
                <div className={styles.chartHeader}>
                  <span className={styles.chartTitle}>Реакции / Комментарии / Репосты</span>
                  <span className={styles.chartHint}>по дням публикации</span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartSeriesData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
                    <YAxis width={28} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                    <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="likes" stroke={PLATFORM_COLOR}
                      dot={{ r: 2 }} strokeWidth={2} name="Реакции" connectNulls />
                    <Line type="monotone" dataKey="comments" stroke="#f59e0b"
                      dot={{ r: 2 }} strokeWidth={1.5} name="Комментарии" connectNulls />
                    <Line type="monotone" dataKey="reposts" stroke="#22c55e"
                      dot={{ r: 2 }} strokeWidth={1.5} name="Репосты" connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                  <span className={styles.chartTitle}>Посты по рубрикам</span>
                  <span className={styles.chartHint}>% от кол-ва</span>
                </div>
                {catCounts.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={catCounts} cx="50%" cy="45%" innerRadius={55} outerRadius={80}
                        dataKey="value" nameKey="name">
                        {catCounts.map((entry, i) => (
                          <Cell key={i} fill={entry.color || PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className={styles.chartEmpty}>Нет рубрик</div>}
              </div>
            </div>
          )}

          {/* ── Posts table ──────────────────────────────────────────────── */}
          {posts.length > 0 && (
            <section className={styles.section}>
              {catSaveMsg && (
                <div style={{ fontSize: 11, marginBottom: 6, color: catSaveMsg === 'ok' ? 'var(--success)' : 'var(--danger)' }}>
                  {catSaveMsg === 'ok' ? '✓ Рубрика сохранена' : `Ошибка: ${catSaveMsg.replace('err:', '')}`}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
                <h2 className={styles.sectionTitle} style={{ margin: 0 }}>Публикации ({postsTotal})</h2>
                <div className={styles.filters} style={{ flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Рубрика:</span>
                  <select className="form-input" value={postsCategoryId ?? ''}
                    onChange={e => { setPostsCategoryId(e.target.value ? Number(e.target.value) : null) }}
                    style={{ fontSize: 12, padding: '3px 8px', height: 28, width: 'auto' }}>
                    <option value="">Все рубрики</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <SortTh col="published_at" label="Пост" current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                      <th>Рубрика</th>
                      <SortTh col="views_1" label={<>Просмотры <TT k="Просмотры" /></>} current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                      <SortTh col="views_24h" label={<>за 24ч <TT k="Просмотры за 24ч" /></>} current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                      <SortTh col="likes" label={<>Реакции <TT k="Реакции" /></>} current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                      <th>Комм. <TT k="Комментарии" /></th>
                      <SortTh col="reposts" label={<>Репосты <TT k="Репосты" /></>} current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                      <th>ViewsRate <TT k="ViewsRate" /></th>
                      <SortTh col="erviews" label={<>ERViews <TT k="ERViews" /></>} current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                      <SortTh col="er" label={<>ER <TT k="ER" /></>} current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((p) => {
                      const cat = categories.find((c) => c.id === p.category_id)
                      return (
                        <tr key={p.id}>
                          <td className={styles.postCell}>
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block' }}>
                              {formatDate(p.published_at)}
                            </span>
                            {p.post_url ? (
                              <a href={p.post_url} target="_blank" rel="noopener noreferrer" className={styles.postLink}>
                                {p.text_preview || '(без текста)'}
                              </a>
                            ) : <span>{p.text_preview || '(без текста)'}</span>}
                          </td>
                          <td>
                            <select
                              disabled={!canManage}
                        value={p.category_id ?? ''}
                              onChange={e => handleCategoryChange(p.id, e.target.value ? Number(e.target.value) : null)}
                              className={styles.catSelect}
                              style={cat ? { borderColor: cat.color, color: cat.color } : undefined}
                            >
                              <option value="">—</option>
                              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </td>
                          <td>{formatNumber(p.views)}</td>
                          <td>
                            {p.views_24h
                              ? formatNumber(p.views_24h)
                              : p.views_24h_pending
                                ? <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>в процессе</span>
                                : '—'}
                          </td>
                          <td>{formatNumber(p.reactions)}</td>
                          <td>{formatNumber(p.comments)}</td>
                          <td>{formatNumber(p.reposts)}</td>
                          <td>{p.views_rate != null ? `${p.views_rate}%` : '—'}</td>
                          <td>{p.erviews != null ? `${p.erviews}%` : '—'}</td>
                          <td>{p.er != null ? `${p.er}%` : '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {posts.length < postsTotal && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={loadingMore}
                    onClick={handleLoadMorePosts}
                    style={{ minWidth: 140, maxWidth: '100%' }}
                  >
                    {loadingMore ? <span className="spinner" /> : `Показать ещё (${postsTotal - posts.length})`}
                  </button>
                </div>
              )}
            </section>
          )}

          {/* ── MoM Table ────────────────────────────────────────────────── */}
          {momData && momData.months?.length > 0 && (
            <section className={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h2 className={styles.sectionTitle}>Сравнение по месяцам</h2>
                <PeriodSelector options={MOM_OPTIONS} value={momMonths} onChange={v => setMomMonths(Number(v))} />
              </div>
              <div ref={momTableRef} className={`${styles.tableWrapper} ${styles.momTableWrapper}`}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 200 }}>Метрика</th>
                      {momData.months.map((m) => (
                        <th key={`${m.year}-${m.month}`}>{m.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {momData.rows.map((row) => (
                      <tr key={row.label}>
                        <td className={styles.momMetric}>
                          {row.label}
                          {METRIC_META[row.label] && (
                            <MetricTooltip
                              description={METRIC_META[row.label].desc}
                              source={METRIC_META[row.label].src}
                            />
                          )}
                        </td>
                        {row.values.map((val, idx) => {
                          if (row.fmt === 'raw') {
                            return <td key={idx} className={styles.momCell}>{val ?? '—'}</td>
                          }
                          const prev = idx > 0 ? row.values[idx - 1] : null
                          const delta = (val !== null && prev !== null && prev !== 0)
                            ? Math.round((val - prev) / Math.abs(prev) * 100)
                            : null
                          const invDelta = row.inverse ? (delta !== null ? -delta : null) : delta

                          let formatted
                          if (val === null || val === undefined) {
                            formatted = '—'
                          } else if (row.fmt === 'percent') {
                            formatted = `${val}%`
                          } else if (row.fmt === 'full') {
                            formatted = formatFull(val)
                          } else {
                            formatted = formatNumber(val)
                          }

                          return (
                            <td key={idx} className={styles.momCell}>
                              <span className={styles.momValue}>{formatted}</span>
                              {delta !== null && idx > 0 && (
                                <span className={`${styles.momDelta} ${
                                  invDelta > 0 ? styles.deltaPos
                                    : invDelta < 0 ? styles.deltaNeg
                                    : styles.deltaZero
                                }`}>
                                  {invDelta > 0 ? '↑' : invDelta < 0 ? '↓' : '='}{Math.abs(delta)}%
                                </span>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function SortTh({ col, label, current, dir, onSort }) {
  const active = current === col
  return (
    <th onClick={() => onSort(col)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ opacity: active ? 1 : 0.7 }}>{label}</span>{' '}
      <span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (dir === 'desc' ? '↓' : '↑') : '↕'}</span>
    </th>
  )
}
